import { FC, ReactElement, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ICON_NAMES } from '@belong/types';
import { GLOBAL_BACK_TO_TOP_PORTAL, GLOBAL_BACK_TO_TOP_TRIGGER } from '../../../helpers/testIds';
import { useBackToTopVisibility } from '../../hooks/useBackToTopVisibility';
import { Icon } from '../../Icon';
import { Prompt, Root } from './BackToTopTrigger.styles';
import { getFirstFocusableElementInMain } from './getFirstFocusableElementInMain';
import { manageFocusAtTop } from './manageFocusAtTop';

interface IProps {
  appearanceThreshold?: number;
}

const BackToTopButton: FC<IProps> = ({
  appearanceThreshold = 4 // how many screen heights (100vh) the user must scroll before showing
}: IProps) => {
  const { isHidden } = useBackToTopVisibility(appearanceThreshold);

  const rootNode = useRef<HTMLButtonElement | null>(null);

  /**
   * When 'back to top' button is clicked scroll to top and
   * move focus to a visible element at the top so it is intuitive
   * specially for sighted keyboard users
   */
  const scrollToTop = () => {
    const element = getFirstFocusableElementInMain();
    const focusElement = () => element?.focus({ preventScroll: true });
    const focusAtTop = () => {
      if (element) {
        requestAnimationFrame(() => requestAnimationFrame(focusElement));
      }
    };

    // Avoid smooth scrolling for users who prefer reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.scrollTo(0, 0);
      focusAtTop();
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    manageFocusAtTop(focusAtTop);
  };

  const handleClick = () => {
    if (rootNode.current) {
      rootNode.current.blur();
    }

    scrollToTop();
  };

  return (
    <Root
      ref={rootNode as any}
      onClick={handleClick}
      hidden={isHidden}
      data-testid={GLOBAL_BACK_TO_TOP_TRIGGER}
      aria-label="Back to top"
    >
      <Icon name={ICON_NAMES.ArrowUp} size="xsmall" />
      <Prompt>Top</Prompt>
    </Root>
  );
};

BackToTopButton.displayName = 'BackToTopButton';

export const BackToTopTrigger: FC<IProps> = (props: IProps) => {
  const [mountNode, setMountNode] = useState<HTMLElement | null>(null);

  /**
   * We mount this component at the very end of the document, right before the
   * `</body>` so it will be the most-on-top at all times.
   */
  useEffect(() => {
    const portalMountNode = document.createElement('div');

    // adding basic styling to align the backToTop button to the right
    portalMountNode.style.display = 'flex';
    portalMountNode.style.flexDirection = 'row';
    portalMountNode.style.justifyContent = 'right';
    portalMountNode.style.height = '0';

    portalMountNode.dataset.testid = GLOBAL_BACK_TO_TOP_PORTAL;

    const main = document.querySelector('main');

    if (main) {
      main.append(portalMountNode);
      setMountNode(portalMountNode);
    }

    return () => {
      setMountNode(null);
      const nodeToRemove = document.querySelector(`[data-testid="${GLOBAL_BACK_TO_TOP_PORTAL}"]`);
      if (nodeToRemove) {
        nodeToRemove.remove();
      }
    };
  }, [setMountNode]);

  if (!mountNode) {
    return null;
  }

  return createPortal(<BackToTopButton {...props} />, mountNode) as ReactElement;
};

BackToTopTrigger.displayName = 'BackToTopTrigger';

export default BackToTopTrigger;
